import { useEffect, useState } from 'react';
import { Box, Container } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { datadogRum } from '@datadog/browser-rum';
import ReFormComponent, {
  type JsonSchema,
  type NestedObject,
  type UISchemaElement,
} from '@restore/reform';
import { useSettings } from 'src/hooks/use-settings';
import DocumentService from 'src/services/documentService';
import { getAccessToken, getRealmName } from 'src/routes/AuthContext';
import CommonLoader from 'src/utils/common-loader';
import SucessModal from '../sucess-modal';
import {
  errorForLoaderDocumentation,
  successForLoaderDocumentation,
} from 'src/redux/actions/documentationAction';
import {
  baseApiUrl,
  aiBaseApiUrl,
  codesApiUrl,
  codesApiSecurityCode,
  reformDsnAiTreatment,
  reformWpnAiTreatment,
  profilityAiTreatment,
  goalBuilder,
  goalBuilderOt,
  goalBuilderPtAmbulation,
  slpEvalReformOAMigrationTreatment,
  ptRecertReformOAMigrationTreatment,
  excludes1WarnTreatment,
  icd10ReturnToProvider,
  dsnUnitEngineV2,
} from 'src/config';
import useCurrentUser from 'src/hooks/use-current-user';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { ReformJsonSchema, ReformUISchema } from 'src/services/documentService/getSchema';

type FormStatus = 'loading' | 'ready' | 'submitting' | 'success' | 'error' | 'noPayerError';

type Discipline = 'PT' | 'OT' | 'SLP';

interface ReFormProps {
  formId: string;
  uuid: string;
  disablePadding?: boolean;
  selectedPatient?: any;
  onSubmitBlocked?: () => void;
  onSubmitError?: (error: {
    error?: string;
    errorCode?: string;
    errorData?: any;
    episodeOfCareId?: string;
  }) => void;
  isSupervisorEdit?: boolean;
  onBeforeSupervisorSubmit?: () => Promise<{ proceed: boolean; note?: string }>;
  // THIN-8038: lets the SOAP-note SupervisorSelector fetch the facility's eligible
  // supervisors (assistants/students whose settings require supervision). Off by
  // default so a therapist never gets a peer auto-filled as their supervisor.
  supervisorSelectionEnabled?: boolean;
}

const ReForm: React.FC<ReFormProps> = (props) => {
  const {
    formId,
    uuid,
    disablePadding,
    selectedPatient,
    onSubmitBlocked,
    onSubmitError,
    isSupervisorEdit,
    onBeforeSupervisorSubmit,
    supervisorSelectionEnabled,
  } = props;

  const settings = useSettings();
  const [autosaveDate, setAutosaveDate] = useState<Date | undefined>();
  const [schema, setSchema] = useState<ReformJsonSchema>();
  const [uischema, setUischema] = useState<ReformUISchema>();
  const [prefill, setPrefill] = useState<NestedObject>();
  const [formStatus, setFormStatus] = useState<FormStatus>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('Something went wrong');
  const [formSubmissionUuid, setFormSubmissionUuid] = useState<string | false>(false);
  const dispatch = useDispatch();
  const realm = getRealmName();

  const location = useLocation();
  const getFacilityUuid = localStorage.getItem('facilityId') || '';
  const [discipline, providerId] = useCurrentUser(['discipline', 'providerId']);

  const activeTabFromRedux = useSelector((state: any) => state.switchDisciplineReducer);

  const [reformDsnAiEnabled] = useFeatureToggleWhenReady(reformDsnAiTreatment, 'FACILITY');

  const [reformWpnAiEnabled] = useFeatureToggleWhenReady(reformWpnAiTreatment, 'FACILITY');

  const [profilityAiEnabled] = useFeatureToggleWhenReady(profilityAiTreatment, 'FACILITY');

  const [goalBuilderEnabled] = useFeatureToggleWhenReady(goalBuilder, 'FACILITY');

  const [goalBuilderOtEnabled] = useFeatureToggleWhenReady(goalBuilderOt, 'FACILITY');
  const [goalBuilderPtAmbulationEnabled] = useFeatureToggleWhenReady(
    goalBuilderPtAmbulation,
    'FACILITY'
  );

  const [slpEvalReformOAMigrationEnabled] = useFeatureToggleWhenReady(
    slpEvalReformOAMigrationTreatment,
    'FACILITY'
  );

  const [ptRecertReformOAMigrationEnabled] = useFeatureToggleWhenReady(
    ptRecertReformOAMigrationTreatment,
    'FACILITY'
  );

  const [excludes1WarnEnabled] = useFeatureToggleWhenReady(excludes1WarnTreatment, 'FACILITY');

  const [icd10ReturnToProviderEnabled] = useFeatureToggleWhenReady(
    icd10ReturnToProvider,
    'FACILITY'
  );

  const [unitEngineV2Enabled] = useFeatureToggleWhenReady(dsnUnitEngineV2, 'FACILITY');

  const patientId = useSelector((state: any) => state?.getPatientIdReducer);

  // Get scheduled documents from Redux state for the service to use
  const reduxScheduledDocuments = useSelector(
    (state: any) => state.getEpisodeOfCareRequiredDocumentReducer?.response?.content
  );

  // Fetch the correct formSubmissionUuid using the service
  // Service handles Redux state first, then API fallback, then fallbackUuid
  useEffect(() => {
    const fetchFormSubmissionUuid = async () => {
      const result = await DocumentService.getFormSubmissionUuid({
        episodeOfCareId: location?.state?.episodeOfCareId,
        formId,
        uuid,
        activeTab: location?.state?.activeTab || activeTabFromRedux || discipline,
        providerId,
        reduxScheduledDocuments,
      });

      setFormSubmissionUuid(result.formSubmissionUuid);
    };

    fetchFormSubmissionUuid();
  }, [
    formId,
    uuid,
    location?.state,
    discipline,
    providerId,
    activeTabFromRedux,
    reduxScheduledDocuments,
  ]);

  useEffect(() => {
    if (formStatus === 'loading') {
      if (formId && realm) {
        DocumentService.getSchema(realm, formId, uuid)
          .then((response) => {
            if (response.error) {
              setFormStatus('error');
              setErrorMessage(response.error);
            } else {
              // Validate patient data match before setting form data
              const isDailySessionNote = location.pathname.includes('daily-session-notes');

              if (isDailySessionNote && selectedPatient && response.data?.generalInformation) {
                const expectedPatientFirstName = selectedPatient.firstName.trim().toLowerCase();
                const expectedPatientLastName = selectedPatient.lastName.trim().toLowerCase();

                const actualPatientFirstName = response.data.generalInformation.patientFirstName
                  .trim()
                  .toLowerCase();
                const actualPatientLastName = response.data.generalInformation.patientLastName
                  .trim()
                  .toLowerCase();

                if (
                  actualPatientFirstName &&
                  actualPatientFirstName !== expectedPatientFirstName &&
                  actualPatientLastName &&
                  actualPatientLastName !== expectedPatientLastName
                ) {
                  datadogRum.addAction('dsn_patient_mismatch', {
                    formId,
                    realm,
                    expectedPatient: `${expectedPatientFirstName} ${expectedPatientLastName}`,
                    actualPatient: `${actualPatientFirstName} ${actualPatientLastName}`,
                    error: 'Patient data mismatch detected',
                  });

                  setFormStatus('error');
                  setErrorMessage('Patient data mismatch detected. Please refresh and try again.');
                  return;
                }
              }

              // Clear any previous validation errors and set form data
              setSchema(response.schema);
              setUischema(response.uiSchema);
              setPrefill(response.data);
              setAutosaveDate(response?.data?.metadata?.lastSavedAt || undefined);
              const metadataFormSubmissionUuid = response?.data?.metadata?.formSubmissionUuid;
              if (typeof metadataFormSubmissionUuid === 'string' && metadataFormSubmissionUuid) {
                setFormSubmissionUuid(metadataFormSubmissionUuid);
              }
              setFormStatus('ready');
            }
          })
          .catch((error) => {
            setFormStatus('error');
          });
      }
    }
  }, [formStatus, formId, uuid, realm, location, selectedPatient]);

  let component;

  const submitCallback = async (data: JsonSchema) => {
    if (onSubmitBlocked) {
      onSubmitBlocked();
      return;
    }
    if (formId && realm) {
      let supervisorNote: string | undefined;
      if (isSupervisorEdit && onBeforeSupervisorSubmit) {
        const gate = await onBeforeSupervisorSubmit();
        if (!gate?.proceed) {
          return;
        }
        supervisorNote = gate?.note;
      }

      datadogRum.addAction('submitCallback_STARTED', { formId, realm });
      setFormStatus('submitting');

      const formType = (data as any)?.RF_METADATA?.formType || '';

      const submitFn = isSupervisorEdit
        ? DocumentService.submitReformSupervisor(
            realm,
            formId,
            uuid,
            data,
            getFacilityUuid,
            supervisorNote
          )
        : DocumentService.submitReform(realm, formId, uuid, data, getFacilityUuid, formType);

      submitFn
        .then((result) => {
          if (result.success) {
            datadogRum.addAction('submitCallback_SUCCESS', { formId, realm });
            setFormStatus('success');
          } else {
            datadogRum.addAction('submitCallback_FAILED', { formId, realm });
            // Check if this is a specific error that should be handled by parent component
            if (result.errorCode === 'NO_PAYER_ON_CERTIFICATION_START_DATE' && onSubmitError) {
              onSubmitError({
                error: result.error,
                errorCode: result.errorCode,
                errorData: result.errorData,
                episodeOfCareId: location?.state?.episodeOfCareId,
              });
              // Reset form status to ready so user can see the form again
              setFormStatus('noPayerError');
              return;
            }
            setErrorMessage(result.error || 'Something went wrong');
            setFormStatus('error');
          }
        })
        .catch((error) => {
          datadogRum.addAction('submitCallback_FAILED', { formId, realm, error });
          setErrorMessage(error.message || 'Something went wrong');
          setFormStatus('error');
        });
    } else {
      datadogRum.addAction('submitCallback_FAILED', { formId, realm });
    }
  };

  const autosaveCallback = async (data: JsonSchema): Promise<boolean> => {
    if (formId && realm) {
      datadogRum.addAction('autosaveCallback_STARTED', { formId, realm });

      return DocumentService.autosave(realm, formId, uuid, data).then((response) => {
        if (response) {
          datadogRum.addAction('autosaveCallback_SUCCESS', { formId, realm, uuid });
          return response;
        } else {
          datadogRum.addAction('autosaveCallback_FAILED', { formId, realm });
          return false;
        }
      });
    } else {
      return false;
    }
  };

  // Best-effort save when the page is hidden/unloading. Reform calls this
  // synchronously and only when the form is dirty + within the keepalive size
  // limit; it dispatches a fire-and-forget keepalive request that survives page
  // teardown. The returned boolean reports dispatch only (the response can't be
  // awaited at unload); a false return lets Reform recover via the normal
  // autosave when the page is merely hidden and survives.
  const unloadSaveCallback = (data: JsonSchema): boolean => {
    if (!formId || !realm) {
      return false;
    }

    datadogRum.addAction('unloadSaveCallback_DISPATCHED', { formId, realm, uuid });
    return DocumentService.autosaveKeepalive(realm, formId, uuid, data);
  };

  const handleSuccess = () => {
    dispatch(successForLoaderDocumentation('Success'));
  };

  const handleError = () => {
    dispatch(errorForLoaderDocumentation('Error'));
  };

  useEffect(() => {
    if (
      prefill?.generalInformation?.formSubmissionUuid &&
      formSubmissionUuid &&
      formSubmissionUuid !== prefill.generalInformation.formSubmissionUuid
    ) {
      datadogRum.addAction('formSubmissionUuid_mismatch', {
        fromUseEffect: formSubmissionUuid,
        fromPrefill: prefill.generalInformation.formSubmissionUuid,
      });
    }
  }, [prefill, formSubmissionUuid]);

  switch (formStatus) {
    case 'loading':
      component = <CommonLoader />;
      break;

    case 'ready': {
      // patientId + formSubmissionUuid are consumed by the reform Icd10 Excludes1 warning hook
      // to filter out the current evaluation's own codes from the cross-eval set. They're only
      // recognized by @restore/reform >= the BRK-1686 release; cast to any so older typings
      // don't error during the staged rollout.
      const excludes1Props = {
        patientId: typeof patientId === 'string' && patientId.length > 0 ? patientId : undefined,
        formSubmissionUuid: typeof formSubmissionUuid === 'string' ? formSubmissionUuid : undefined,
      } as any;

      // BRK-2170: unitEngineV2 is only recognized by @restore/reform >= the BRK-2169 release; spread
      // in so older typings don't flag it as an unknown toggle during the staged rollout.
      const unitEngineV2Toggle = { unitEngineV2: unitEngineV2Enabled || false };

      component = (
        <ReFormComponent
          schema={schema as JsonSchema}
          uiSchema={uischema as UISchemaElement}
          submitCallback={submitCallback}
          initialData={prefill}
          autosaveCallback={autosaveCallback}
          unloadSaveCallback={unloadSaveCallback}
          autosaveDate={autosaveDate}
          getAccessToken={getAccessToken}
          apiUrl={baseApiUrl}
          aiApiUrl={aiBaseApiUrl}
          codesApiUrl={codesApiUrl}
          codesApiSecurityCode={codesApiSecurityCode}
          realm={realm || ''}
          facilityId={getFacilityUuid}
          discipline={discipline as Discipline}
          supervisorSelectionEnabled={Boolean(supervisorSelectionEnabled)}
          compact={true}
          formUuid={uuid}
          {...excludes1Props}
          featureToggles={{
            ...unitEngineV2Toggle,
            dsnAi: reformDsnAiEnabled || false,
            wpnAi: reformWpnAiEnabled || false,
            profilityAi: profilityAiEnabled || false,
            goalBuilder: goalBuilderEnabled || false,
            goalBuilderOt: goalBuilderOtEnabled || false,
            goalBuilderPtAmbulation: goalBuilderPtAmbulationEnabled || false,
            slpEvalReformMigration: slpEvalReformOAMigrationEnabled || false,
            ptRecertReformMigration: ptRecertReformOAMigrationEnabled || false,
            icd10Excludes1Warning: excludes1WarnEnabled || false,
            icd10RtpWarning: icd10ReturnToProviderEnabled || false,
            // BRK-2110: hard-coded on until this gets a Split treatment.
            groupCodeNoteTemplate: true,
          }}
        />
      );
      break;
    }

    case 'submitting':
      component = <CommonLoader />;
      break;

    case 'success':
      component = (
        <SucessModal
          title="Submitted"
          message="Your submission has been received"
          close={handleSuccess}
        />
      );
      break;

    case 'noPayerError':
      component = <></>;
      break;

    default:
      component = (
        <SucessModal
          title="Error"
          message={errorMessage}
          isSucess={false}
          close={handleError}
        />
      );

      break;
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        paddingX: disablePadding ? 0 : 'calc(4rem - 24px)',
        position: formStatus === 'ready' ? 'relative' : 'initial',
        zIndex: 1,
      }}
    >
      {component}
    </Box>
  );
};

export default ReForm;
